/*
 * CONTRARIAN LAYER — Spatial Audio Engine
 *
 * Web Audio API + HRTF panning. Ambient sub-drone chord that breathes
 * with the experience. Mouse position drives HRTF spatial position.
 * Six season-specific harmonic layers crossfade as the user scrolls.
 */

const DRONE_FREQS = [40, 60.07, 80.31, 120.71, 40.09, 60.19];

const SEASON_HARMONICS = [
  [220, 277.2, 330],
  [246.9, 311, 370],
  [261.6, 329.6, 392],
  [293.7, 370, 440],
  [329.6, 415.3, 493.9],
  [196, 261.6, 329.6],
];

class SpatialAudioEngine {
  #ctx = null;
  #master = null;
  #panner = null;
  #droneOscs = [];
  #harmonicOscs = [];
  #harmonicGains = [];
  #seasonGain = null;
  #started = false;
  #currentSeason = 0;

  async start() {
    if (this.#started) return;
    this.#started = true;

    this.#ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.#ctx.state === 'suspended') await this.#ctx.resume();

    this.#master = this.#ctx.createGain();
    this.#master.gain.setValueAtTime(0, this.#ctx.currentTime);
    this.#master.gain.linearRampToValueAtTime(0.055, this.#ctx.currentTime + 4);
    this.#master.connect(this.#ctx.destination);

    this.#panner = this.#ctx.createPanner();
    this.#panner.panningModel = 'HRTF';
    this.#panner.distanceModel = 'inverse';
    this.#panner.refDistance = 1;
    this.#panner.positionZ.value = 2;

    const reverb = await this.#buildReverb(6.5);
    const reverbGain = this.#ctx.createGain();
    reverbGain.gain.value = 0.45;

    const filter = this.#ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;
    filter.Q.value = 0.8;

    DRONE_FREQS.forEach((freq, i) => {
      const osc = this.#ctx.createOscillator();
      const g = this.#ctx.createGain();

      osc.type = i < 2 ? 'sine' : 'sawtooth';
      osc.frequency.value = freq;

      const lfo = this.#ctx.createOscillator();
      const lfoG = this.#ctx.createGain();
      lfo.frequency.value = 0.04 + i * 0.009;
      lfoG.gain.value = freq * 0.003;
      lfo.connect(lfoG);
      lfoG.connect(osc.frequency);
      lfo.start();

      g.gain.value = 0.06 / DRONE_FREQS.length;
      osc.connect(g);
      g.connect(filter);
      osc.start();
      this.#droneOscs.push(osc);
    });

    this.#seasonGain = this.#ctx.createGain();
    this.#seasonGain.gain.value = 0;

    SEASON_HARMONICS.forEach((chord, si) => {
      const sg = this.#ctx.createGain();
      sg.gain.value = 0;
      this.#harmonicGains.push(sg);

      chord.forEach((freq, hi) => {
        const osc = this.#ctx.createOscillator();
        const g = this.#ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        g.gain.value = 0.025 / chord.length;
        osc.connect(g);
        g.connect(sg);
        osc.start();
        this.#harmonicOscs.push(osc);
      });

      sg.connect(this.#seasonGain);
    });

    filter.connect(this.#panner);
    this.#seasonGain.connect(this.#panner);

    this.#panner.connect(reverb);
    this.#panner.connect(reverbGain);
    reverb.connect(reverbGain);
    reverbGain.connect(this.#master);

    this.#setSeason(0);
    setTimeout(() => {
      if (this.#seasonGain) {
        this.#seasonGain.gain.linearRampToValueAtTime(
          0.7, this.#ctx.currentTime + 3
        );
      }
    }, 2000);
  }

  async #buildReverb(duration) {
    const sr = this.#ctx.sampleRate;
    const len = Math.floor(sr * duration);
    const buf = this.#ctx.createBuffer(2, len, sr);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
      }
    }
    const conv = this.#ctx.createConvolver();
    conv.buffer = buf;
    return conv;
  }

  #setSeason(index) {
    const t = this.#ctx.currentTime;
    const fadeTime = 2.5;
    this.#harmonicGains.forEach((g, i) => {
      g.gain.cancelScheduledValues(t);
      g.gain.setTargetAtTime(i === index ? 1 : 0, t, fadeTime / 3);
    });
    this.#currentSeason = index;
  }

  season(index) {
    if (!this.#ctx || index === this.#currentSeason) return;
    this.#setSeason(index);
  }

  pan(x, y) {
    if (!this.#panner || !this.#ctx) return;
    const px = (x / window.innerWidth - 0.5) * 5;
    const py = -(y / window.innerHeight - 0.5) * 3;
    const t = this.#ctx.currentTime;
    this.#panner.positionX.setTargetAtTime(px, t, 0.2);
    this.#panner.positionY.setTargetAtTime(py, t, 0.2);
  }

  pulse(intensity = 1) {
    if (!this.#master || !this.#ctx) return;
    const t = this.#ctx.currentTime;
    const cur = this.#master.gain.value;
    this.#master.gain.setValueAtTime(cur, t);
    this.#master.gain.linearRampToValueAtTime(
      Math.min(0.18, cur + 0.04 * intensity), t + 0.08
    );
    this.#master.gain.setTargetAtTime(0.055, t + 0.1, 0.8);
  }

  stop() {
    if (!this.#ctx) return;
    this.#master.gain.linearRampToValueAtTime(0, this.#ctx.currentTime + 2);
    setTimeout(() => this.#ctx.close(), 2500);
  }
}

export const audioEngine = new SpatialAudioEngine();
